import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, p, pa, del } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { AccessoStaff } from "@/components/AccessoStaff";
import { SchedaStaff } from "@/components/SchedaStaff";
import { MandaAccesso } from "@/components/MandaAccesso";

const ROLES = ["cameriere", "runner", "cassiere", "sommelier", "direttore", "titolare", "cuoco", "pizzaiolo", "pasticciere", "barista"];
const STATES = ["attivo", "ferie", "malattia", "riposo"];

export default function Dipendenti() {
  const { handleError, refreshPlan, plan } = usePlan();
  const { demoMode, settings } = useApp();
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [link, setLink] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("cameriere");
  const [esempi, setEsempi] = useState(0);

  const load = useCallback(() => {
    g("/staff").then(setList);
    g("/staff/esempi").then((d) => setEsempi(d?.count || 0)).catch(() => setEsempi(0));
  }, []);
  useEffect(() => {
    load();
    g("/tenant/staff-link").then((d) => setLink(d?.url || "")).catch(() => setLink(""));
  }, [load]);

  const add = async () => {
    if (!name.trim()) return toast.error(t("dp.needName"));
    try {
      const s = await p("/staff", { name, role });
      toast.success(t("dp.added", { name: s.name }), {
        description: t("dp.addedSub", { username: s.username, pin: s.pin }),
        duration: 15000,
      });
    } catch (e) {
      if (handleError(e)) return;
      return toast.error(t("dp.addFail"));
    }
    setName("");
    load();
    refreshPlan();
  };

  const limitLabel = () => {
    const m = plan?.limits?.max_users;
    if (!m) return t("dp.unlimited");
    return m === 1 ? t("dp.limitUser", { n: m }) : t("dp.limitUsers", { n: m });
  };

  return (
    <div className="fade-up space-y-5" data-testid="dipendenti-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("g.owner")}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("dp.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {demoMode && t("dp.demoNote")}
          {plan && (
            <span data-testid="staff-plan-limit">
              {" "}{t("dp.planNote", { plan: plan.plan_name, users: limitLabel(), active: plan.usage.users })}
            </span>
          )}
        </p>
      </div>

      {esempi > 0 && (
        <Card className="border-amber-300 bg-amber-50 p-5" data-testid="staff-esempi-card">
          <p className="text-base font-bold text-amber-900">
            {esempi} persone di esempio: non sono tue
          </p>
          <p className="mt-1 text-sm text-amber-900/80">
            Arrivano dalla demo con cui hai provato il gestionale, non sono mai entrate e il loro
            codice è pubblico. Togliile e aggiungi la tua squadra qui sotto: una persona per volta,
            il PIN lo scrive l'app e lo mandi su WhatsApp.
          </p>
          <Button
            className="mt-3 h-11 bg-amber-900 hover:bg-amber-800"
            data-testid="staff-togli-esempi-btn"
            onClick={async () => {
              if (!window.confirm(`Rimuovo ${esempi} persone di esempio? Chi hai già rinominato o è già entrato non viene toccato.`)) return;
              try {
                const r = await del("/staff/esempi");
                toast.success(`Rimosse ${r.removed} persone di esempio`);
                setEsempi(0);
                load();
              } catch {
                toast.error("Non è stato possibile rimuoverle");
              }
            }}
          >
            <Trash2 className="mr-1.5 h-4 w-4" /> Rimuovi le persone di esempio
          </Button>
        </Card>
      )}

      <Card className="p-5" data-testid="staff-guida-card">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Come si fa</p>
        <ol className="mt-2 space-y-1.5 text-sm text-slate-600">
          <li><span className="font-semibold text-slate-900">1.</span> Scrivi nome e cognome, scegli il ruolo e tocca «Aggiungi».</li>
          <li><span className="font-semibold text-slate-900">2.</span> Tocca <span className="font-semibold">«Manda l'accesso»</span> sulla sua scheda: l'app crea il PIN, prepara il link del locale e il messaggio già scritto per WhatsApp (o l'email, se non ha WhatsApp).</li>
          <li><span className="font-semibold text-slate-900">3.</span> La persona apre il link, tocca il suo nome, digita il PIN e salva il link sulla schermata Home. Il PIN si vede solo al momento dell'invio: se lo perde, premi ancora «Manda l'accesso».</li>
        </ol>
      </Card>

      <AccessoStaff />

      <Card className="flex flex-wrap items-center gap-2 p-5" data-testid="add-staff-bar">
        <Input className="h-10 w-56" placeholder={t("dp.namePh")} value={name} onChange={(e) => setName(e.target.value)} data-testid="staff-name-input" />
        <div className="flex flex-wrap gap-1">
          {ROLES.map((r) => (
            <Button key={r} size="sm" variant={role === r ? "default" : "outline"} onClick={() => setRole(r)} data-testid={`staff-role-${r}`} className="capitalize">
              {r}
            </Button>
          ))}
        </div>
        <Button onClick={add} data-testid="add-staff-btn">
          <Plus className="mr-1.5 h-4 w-4" /> {t("dp.add")}
        </Button>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {list.map((s) => (
          <Card key={s.id} className={`p-5 ${s.active ? "" : "opacity-60"}`} data-testid={`staff-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold">{s.name}</p>
                <p className="text-xs text-slate-500">
                  {s.role_label}
                  {s.department && ` · ${s.department}`}
                  {!s.can_send && ` · ${t("dp.monitorOnly")}`}
                  {s.alt_role && ` · anche ${s.alt_role_label}`}
                </p>
                <p className="font-num mt-1 text-xs text-slate-600">
                  {s.username} · {s.pin ? `PIN ${s.pin}` : t("dp.pinHidden")}
                  {s.locked ? ` · ${t("dp.locked")}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">{t("dp.active")}</span>
                <Switch
                  checked={s.active}
                  data-testid={`staff-active-${s.id}`}
                  onCheckedChange={async (v) => {
                    try {
                      await pa(`/staff/${s.id}`, { active: v });
                    } catch (e) {
                      if (handleError(e)) return;
                      return toast.error(t("dp.editFail"));
                    }
                    load();
                    refreshPlan();
                  }}
                />
              </div>
            </div>
            <div className="mt-3 rounded-lg bg-slate-50 p-3" data-testid={`staff-alt-role-${s.id}`}>
              <p className="text-[11px] font-semibold text-slate-600">Seconda mansione (facoltativa)</p>
              <p className="text-[11px] text-slate-500">
                Chi fa due lavori — a pranzo in cucina, a cena in sala — con un solo PIN: all'ingresso
                scegle dove lavora adesso.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button
                  data-testid={`staff-alt-none-${s.id}`}
                  onClick={async () => { await pa(`/staff/${s.id}`, { alt_role: "", role: s.role }); load(); }}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    !s.alt_role ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"
                  }`}
                >
                  Nessuna
                </button>
                {ROLES.filter((r) => r !== s.role).map((r) => (
                  <button
                    key={r}
                    data-testid={`staff-alt-${s.id}-${r}`}
                    onClick={async () => { await pa(`/staff/${s.id}`, { alt_role: r, role: s.role }); load(); }}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${
                      s.alt_role === r ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {STATES.map((st) => (
                <button
                  key={st}
                  data-testid={`staff-state-${s.id}-${st}`}
                  onClick={async () => {
                    await pa(`/staff/${s.id}`, { state: st });
                    load();
                  }}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    s.state === st ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {t(`dp.state.${st}`)}
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3" data-testid={`staff-float-${s.id}`}>
              <div>
                <p className="text-[11px] font-semibold text-slate-600">{t("dp.float")}</p>
                <p className="text-[11px] text-slate-500">{t("dp.floatOn")}</p>
              </div>
              <Input
                className="h-9 w-24"
                type="number"
                step="0.01"
                defaultValue={s.float_cash || ""}
                placeholder={t("dp.floatAmount")}
                data-testid={`staff-float-input-${s.id}`}
                onBlur={async (e) => {
                  const v = Number(e.target.value) || 0;
                  if (v === (s.float_cash || 0)) return;
                  await pa(`/staff/${s.id}`, { float_cash: v, has_float: v > 0 });
                  toast.success(t("dp.floatSaved"));
                  load();
                }}
              />
              <Switch
                checked={s.has_float}
                data-testid={`staff-float-switch-${s.id}`}
                onCheckedChange={async (v) => {
                  await pa(`/staff/${s.id}`, { has_float: v });
                  load();
                }}
              />
            </div>
            <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3" data-testid={`staff-shift-${s.id}`}>
              <div>
                <p className="text-[11px] font-semibold text-slate-600">Orario di turno</p>
                <p className="text-[11px] text-slate-500">Serve solo se blocchi l'accesso fuori orario</p>
              </div>
              <Input className="h-9 w-24" type="time" defaultValue={s.shift_from || ""}
                     data-testid={`staff-shift-from-${s.id}`}
                     onBlur={async (e) => {
                       if ((e.target.value || "") === (s.shift_from || "")) return;
                       await pa(`/staff/${s.id}`, { shift_from: e.target.value });
                       load();
                     }} />
              <Input className="h-9 w-24" type="time" defaultValue={s.shift_to || ""}
                     data-testid={`staff-shift-to-${s.id}`}
                     onBlur={async (e) => {
                       if ((e.target.value || "") === (s.shift_to || "")) return;
                       await pa(`/staff/${s.id}`, { shift_to: e.target.value });
                       toast.success(`Turno di ${s.name} aggiornato`);
                       load();
                     }} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <MandaAccesso s={s} onDone={load} />
              {s.pin && <SchedaStaff s={s} locale={settings?.business_name} url={link} lang={lang} />}
              <Button
                size="sm"
                variant="outline"
                data-testid={`staff-newpin-${s.id}`}
                onClick={async () => {
                  const r = await pa(`/staff/${s.id}`, { new_pin: true });
                  toast.success(t("dp.newPinOk", { name: r.name, pin: r.pin }), {
                    description: t("dp.newPinSub"),
                    duration: 15000,
                  });
                  load();
                }}
              >
                <KeyRound className="mr-1.5 h-3.5 w-3.5" /> {t("dp.newPin")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-rose-600"
                data-testid={`staff-delete-${s.id}`}
                onClick={async () => {
                  await del(`/staff/${s.id}`);
                  toast.success(t("dp.removed"));
                  load();
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
